import React, { useEffect, useRef } from 'react';

const BRAND = {
  gold: '#D5B036',
  blue: '#496CB1',
  green: '#2D8C57',
  navy: '#1E2D4B',
};

const NODES = [
  { x: 0.12, y: 0.28, color: BRAND.gold },
  { x: 0.28, y: 0.18, color: BRAND.blue },
  { x: 0.48, y: 0.32, color: BRAND.green },
  { x: 0.72, y: 0.22, color: BRAND.blue },
  { x: 0.88, y: 0.42, color: BRAND.gold },
  { x: 0.62, y: 0.58, color: BRAND.navy },
  { x: 0.38, y: 0.62, color: BRAND.green },
  { x: 0.18, y: 0.72, color: BRAND.blue },
  { x: 0.82, y: 0.74, color: BRAND.green },
];

const EDGES = [
  [0, 1], [1, 2], [2, 3], [3, 4], [2, 5], [5, 6], [6, 7], [5, 8], [4, 8], [1, 6], [3, 5],
];

function rgba(hex, alpha) {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function drawDotGrid(ctx, w, h) {
  ctx.save();
  ctx.fillStyle = rgba(BRAND.navy, 0.04);
  const step = 32;
  for (let x = step; x < w; x += step) {
    for (let y = step; y < h; y += step) {
      ctx.beginPath();
      ctx.arc(x, y, 0.8, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.restore();
}

function getNodePos(node, i, w, h, t) {
  const driftX = Math.sin(t * 0.00055 + i * 1.1) * 0.014;
  const driftY = Math.cos(t * 0.00048 + i * 0.85) * 0.012;
  return {
    px: (node.x + driftX) * w,
    py: (node.y + driftY) * h,
    color: node.color,
  };
}

function drawSignalPulse(ctx, ax, ay, bx, by, t, edgeIndex) {
  const phase = ((t * 0.00035 + edgeIndex * 0.7) % 1);
  const px = ax + (bx - ax) * phase;
  const py = ay + (by - ay) * phase;
  const g = ctx.createRadialGradient(px, py, 0, px, py, 14);
  g.addColorStop(0, rgba(BRAND.gold, 0.28));
  g.addColorStop(1, 'transparent');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(px, py, 14, 0, Math.PI * 2);
  ctx.fill();
}

export default function ContactAmbientBg() {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return undefined;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    let w = 0;
    let h = 0;
    let frameId = 0;
    let time = 0;

    const resize = () => {
      w = wrap.clientWidth;
      h = wrap.clientHeight;
      const dpr = Math.min(window.devicePixelRatio, 2);
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resize();
    window.addEventListener('resize', resize);

    const tick = () => {
      frameId = requestAnimationFrame(tick);
      if (!reducedMotion) time += 16;

      ctx.clearRect(0, 0, w, h);
      drawDotGrid(ctx, w, h);

      const positions = NODES.map((node, i) => getNodePos(node, i, w, h, time));

      EDGES.forEach(([a, b], i) => {
        const na = positions[a];
        const nb = positions[b];
        const cpX = (na.px + nb.px) / 2 + Math.sin(time * 0.0009 + i) * 28;
        const cpY = (na.py + nb.py) / 2 - 36 + Math.cos(time * 0.0007 + i) * 18;

        ctx.beginPath();
        ctx.moveTo(na.px, na.py);
        ctx.quadraticCurveTo(cpX, cpY, nb.px, nb.py);

        const grad = ctx.createLinearGradient(na.px, na.py, nb.px, nb.py);
        grad.addColorStop(0, rgba(na.color, 0.14));
        grad.addColorStop(0.5, rgba(BRAND.blue, 0.2));
        grad.addColorStop(1, rgba(nb.color, 0.14));
        ctx.strokeStyle = grad;
        ctx.lineWidth = 1.1;
        if (!reducedMotion) {
          ctx.setLineDash([5, 9]);
          ctx.lineDashOffset = -((time * 0.025 + i * 4) % 28);
        } else {
          ctx.setLineDash([]);
        }
        ctx.stroke();
        ctx.setLineDash([]);

        if (!reducedMotion && i % 2 === 0) {
          drawSignalPulse(ctx, na.px, na.py, nb.px, nb.py, time, i);
        }
      });

      positions.forEach((n, i) => {
        const pulse = reducedMotion ? 1 : 0.75 + Math.sin(time * 0.002 + i * 0.8) * 0.25;

        ctx.beginPath();
        ctx.arc(n.px, n.py, 10 * pulse, 0, Math.PI * 2);
        ctx.strokeStyle = rgba(n.color, 0.12);
        ctx.lineWidth = 1;
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(n.px, n.py, 4, 0, Math.PI * 2);
        ctx.fillStyle = rgba(n.color, 0.32);
        ctx.fill();

        ctx.beginPath();
        ctx.arc(n.px - 1, n.py - 1, 1.5, 0, Math.PI * 2);
        ctx.fillStyle = rgba('#FFFFFF', 0.7);
        ctx.fill();
      });
    };

    tick();

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <div className="contact-ambient" ref={wrapRef} aria-hidden="true">
      <div className="contact-ambient__orbs">
        <span className="contact-ambient__orb contact-ambient__orb--gold" />
        <span className="contact-ambient__orb contact-ambient__orb--blue" />
        <span className="contact-ambient__orb contact-ambient__orb--green" />
        <span className="contact-ambient__orb contact-ambient__orb--navy" />
      </div>
      <canvas className="contact-ambient__canvas" ref={canvasRef} />
      <div className="contact-ambient__sheen" />
    </div>
  );
}
